import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus } from 'mobx-event-bus2';
import Axios, { AxiosError, AxiosProgressEvent, AxiosResponse, CancelTokenSource } from 'axios';

import Logger from 'utils/logger';

import { ActivityListResponse, ActivityResponse, FileUploadProgressResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';

import { PaginationParams } from 'utils/api/types';
import { UserNameResponse } from 'utils/api/modules/user/user.types';

import { fileTooLargeMessage, isOverUploadLimit } from 'utils/uploadLimits';

import { PAGE_SIZE, SUPPORTED_ACTIVITIES_TYPES, SUPPORTED_ACTIVITIES_EXTENSIONS } from '../../../core/constants';

import { Store } from './types';

export default abstract class DetailsActivities<T extends Store, A> {
  @observable error?: AxiosError;

  @observable isLoading = false;

  @observable activities?: ActivityResponse[];

  @observable hasMore = true;

  private uploadCounter = 0;

  private uploadProgressState = new Map<
    string,
    {
      previousTimestamp: number;
      previousLoaded: number;
    }
  >();

  api: A;

  rootStore: T;

  constructor(rootStore: T, api: A) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.api = api;
  }

  async loadActivitiesData(params: PaginationParams): Promise<AxiosResponse<ActivityListResponse>> {
    throw new Error('loadActivitiesData is not implemented');
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    throw new Error('doUploadFile is not implemented');
  }

  @action.bound
  async loadActivities(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    const params = {
      limit: PAGE_SIZE,
      offset: this.activities ? this.activities.length : 0,
    };
    try {
      this.isLoading = true;
      const response = await this.loadActivitiesData(params);
      const { results, next } = response.data;
      runInAction(() => {
        this.activities = [...(this.activities || []), ...results];
        this.hasMore = !!next;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e as AxiosError<unknown> | undefined;
      });
      Logger.error('Invalid load activities API response', e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  reloadActivities(): void {
    this.error = undefined;
    this.loadActivities();
  }

  @action.bound
  reset(): void {
    if (!this.activities) {
      return;
    }

    this.activities.forEach((activity) => eventBus.unregister(activity));
    this.activities = undefined;
  }

  @action.bound
  addActivity(activity: ActivityResponse): void {
    this.activities = [activity, ...(this.activities || [])];
  }

  @action.bound
  removeActivity(activityId: string): void {
    if (this.activities !== undefined) {
      this.activities = this.activities.filter((activity) => activity.uuid !== activityId);
    }
  }

  @action.bound
  async uploadFile(file: File): Promise<void> {
    if (this.error || !this.activities) {
      return;
    }

    if (isOverUploadLimit(file)) {
      this.rootStore.notification.enqueueErrorSnackbar(fileTooLargeMessage(file));
      return;
    }

    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const isValidType =
      SUPPORTED_ACTIVITIES_TYPES.includes(file.type) || SUPPORTED_ACTIVITIES_EXTENSIONS.includes(fileExtension);
    if (!isValidType) {
      this.rootStore.notification.enqueueErrorSnackbar('File type is not supported. It will not be uploaded');
      return;
    }

    const cancelTokenSource = Axios.CancelToken.source();

    const id = this.uploadCounter;
    this.uploadCounter += 1;
    const uploadActivity = {
      uuid: `upload-task_${id}`,
      type: ActivityType.FileUploadProgress,
      creator: this.rootStore.session?.user as UserNameResponse,
      created: new Date().toISOString(),
      thumbnail: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      loaded: 0,
      onCancel: () => cancelTokenSource.cancel('User cancelled upload'),
    } as FileUploadProgressResponse;

    const onUpdateProgress = (event: AxiosProgressEvent): void => {
      const currentTimestamp = Date.now();
      const progressState = this.uploadProgressState.get(uploadActivity.uuid);
      const updatedActivities = this.activities || [];

      if (updatedActivities.length === 0) {
        this.uploadProgressState.set(uploadActivity.uuid, {
          previousTimestamp: currentTimestamp,
          previousLoaded: 0,
        });
        this.addActivity(uploadActivity);
        return;
      }

      const index = (this.activities || []).findIndex((a) => a.uuid === uploadActivity.uuid);
      if (index === -1) {
        this.uploadProgressState.set(uploadActivity.uuid, {
          previousTimestamp: currentTimestamp,
          previousLoaded: 0,
        });
        this.addActivity(uploadActivity);
        return;
      }

      let speed: number | undefined;
      if (progressState) {
        const timeDiff = (currentTimestamp - progressState.previousTimestamp) / 1000; // Convert to seconds
        const bytesDiff = event.loaded - progressState.previousLoaded;
        if (timeDiff > 0) {
          speed = bytesDiff / timeDiff; // bytes per second
        }
        this.uploadProgressState.set(uploadActivity.uuid, {
          previousTimestamp: currentTimestamp,
          previousLoaded: event.loaded,
        });
      } else {
        this.uploadProgressState.set(uploadActivity.uuid, {
          previousTimestamp: currentTimestamp,
          previousLoaded: event.loaded,
        });
      }

      updatedActivities.splice(index, 1);
      this.addActivity({
        ...uploadActivity,
        loaded: event.loaded,
        speed,
      } as FileUploadProgressResponse);
    };

    try {
      await this.doUploadFile(file, onUpdateProgress, cancelTokenSource);
    } finally {
      this.removeActivity(uploadActivity.uuid);
      this.uploadProgressState.delete(uploadActivity.uuid);
    }
  }
}
